import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Sparkles, Wand2, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { API_URL, BACKEND_URL } from "./backendUrl";

const API = API_URL;

const QUICK_PROMPTS = [
  "Rends le visuel plus propre et plus lisible.",
  "Renforce légèrement le contraste et garde la composition.",
];

const INITIAL_MESSAGES = [
  {
    role: "assistant",
    content: "Décris précisément ce que tu veux corriger. Je régénère ensuite l'image et je remplace la version affichée.",
  },
];

export const ImageEditModal = ({
  jobId,
  image,
  availableImages = [],
  onClose,
  onSubmitEdit,
  quickPrompts = QUICK_PROMPTS,
  showTargetsAccordion = false,
}) => {
  const [currentImage, setCurrentImage] = useState(image);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [prompt, setPrompt] = useState("");
  const [selectedIndices, setSelectedIndices] = useState(() => new Set([image?.index ?? 0]));
  const inputRef = useRef(null);

  useEffect(() => {
    setCurrentImage(image);
    setMessages(INITIAL_MESSAGES);
    setPrompt("");
    setSelectedIndices(new Set([image?.index ?? 0]));
  }, [image]);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [image]);

  const submit = async () => {
    const text = prompt.trim();
    if (!text) return;
    const targets = Array.from(selectedIndices).sort((a, b) => a - b);
    if (targets.length === 0) return;

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: targets.length > 1
          ? `${text}\n\nCibles: ${targets.map((idx) => (idx === 0 ? "originale" : `#${idx}`)).join(", ")}`
          : text,
      },
      {
        role: "assistant",
        content: targets.length > 1
          ? `Retouche lancée sur ${targets.length} image(s). La galerie va se mettre à jour au fil des résultats.`
          : "Retouche lancée. La galerie va se mettre à jour dès que l'image est prête.",
      },
    ]);

    toast.info(targets.length > 1 ? "Retouche multi-variations lancée" : "Retouche lancée");
    if (typeof onSubmitEdit === "function") {
      void onSubmitEdit({ jobId, prompt: text, indices: targets, sourceImage: currentImage });
    }
    onClose();
  };

  const applyQuickPrompt = (value) => setPrompt(value);
  const toggleTarget = (idx) => {
    const next = new Set(selectedIndices);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setSelectedIndices(next);
  };
  const selectAllTargets = () => setSelectedIndices(new Set(targetOptions.map((item) => item.index)));
  const clearTargets = () => setSelectedIndices(new Set());

  if (!currentImage) return null;

  const targetOptions = [
    currentImage,
    ...availableImages.filter((item) => item && item.index !== currentImage.index),
  ].reduce((acc, item) => {
    if (!acc.some((existing) => existing.index === item.index)) acc.push(item);
    return acc;
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-6 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="image-edit-modal"
    >
      <button
        onClick={onClose}
        data-testid="close-image-edit-modal-button"
        className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <div
        className="w-full max-w-[1280px] max-h-[92vh] grid grid-cols-1 lg:grid-cols-[minmax(0,1.25fr)_420px] bg-slate-950/90 border border-white/10 rounded-[28px] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-black p-4 md:p-6 flex flex-col min-h-[60vh]">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Retouche guidée
              </p>
              <h3 className="text-white font-heading text-xl md:text-2xl font-bold mt-2">
                {currentImage.isOriginal || currentImage.index === 0 ? "Image originale" : `Image #${currentImage.index}`}
              </h3>
              <p className="text-white/55 text-sm mt-1">
                La nouvelle version remplace directement la carte dans la galerie.
              </p>
            </div>
            <div className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold uppercase tracking-wider">
              GPT Image 2
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08),transparent_55%)] rounded-[24px] border border-white/10 overflow-hidden p-4">
            <img
              src={`${BACKEND_URL}${currentImage.url}`}
              alt={`Variation ${currentImage.index}`}
              className="max-w-full max-h-[70vh] object-contain rounded-2xl shadow-[0_20px_80px_rgba(0,0,0,0.45)]"
            />
          </div>
        </div>

        <div className="bg-white flex flex-col">
          <div className="p-5 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 flex items-center gap-2">
              <Wand2 className="w-3.5 h-3.5 text-[#f58207]" /> Chat de retouche
            </p>
            <h4 className="text-lg font-bold text-slate-900 mt-2">Décris la correction</h4>
            <p className="text-sm text-slate-500 mt-1">
              Tu peux demander une correction légère ou une vraie retouche ciblée. L'image est régénérée puis replacée ici.
            </p>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
                Exemples rapides
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {quickPrompts.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => applyQuickPrompt(item)}
                    className="px-3 py-2 rounded-full border border-slate-200 bg-slate-50 text-slate-700 text-xs font-medium hover:border-[#f58207] hover:text-[#f58207] transition-colors"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {showTargetsAccordion ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                <Accordion type="single" collapsible defaultValue="targets">
                  <AccordionItem value="targets" className="border-0">
                    <AccordionTrigger className="py-0 text-left no-underline hover:no-underline">
                      <div className="flex w-full items-center justify-between gap-3 pr-2">
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
                          Cibles
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">
                          {selectedIndices.size}/{targetOptions.length}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-0 pt-3">
                      <p className="text-xs text-slate-500">
                        Tu peux appliquer la même retouche à plusieurs variations en une fois.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={selectAllTargets}
                          className="px-3 py-2 rounded-full border text-xs font-medium transition-colors bg-slate-900 text-white border-slate-900 hover:bg-black"
                        >
                          Tout sélectionner
                        </button>
                        <button
                          type="button"
                          onClick={clearTargets}
                          className="px-3 py-2 rounded-full border text-xs font-medium transition-colors bg-white text-slate-700 border-slate-200 hover:border-[#f58207] hover:text-[#f58207]"
                        >
                          Tout désélectionner
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {targetOptions.map((item) => {
                          const active = selectedIndices.has(item.index);
                          return (
                            <button
                              key={item.index}
                              type="button"
                              onClick={() => toggleTarget(item.index)}
                              className={`px-3 py-2 rounded-full border text-xs font-medium transition-colors ${
                                active
                                  ? "bg-[#f58207] text-white border-[#f58207]"
                                  : "bg-slate-50 text-slate-700 border-slate-200 hover:border-[#f58207] hover:text-[#f58207]"
                              }`}
                            >
                              {item.index === 0 ? "Originale" : `Variation #${item.index}`}
                            </button>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
                  Cibles
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Tu peux appliquer la même retouche à plusieurs variations en une fois.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {targetOptions.map((item) => {
                    const active = selectedIndices.has(item.index);
                    return (
                      <button
                        key={item.index}
                        type="button"
                        onClick={() => toggleTarget(item.index)}
                        className={`px-3 py-2 rounded-full border text-xs font-medium transition-colors ${
                          active
                            ? "bg-[#f58207] text-white border-[#f58207]"
                            : "bg-slate-50 text-slate-700 border-slate-200 hover:border-[#f58207] hover:text-[#f58207]"
                        }`}
                      >
                        {item.index === 0 ? "Originale" : `Variation #${item.index}`}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="p-5 flex-1 overflow-y-auto space-y-4 custom-scrollbar bg-slate-50/80">
            {messages.map((msg, index) => (
              <div
                key={`${msg.role}-${index}`}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm ${
                    msg.role === "user"
                      ? "bg-[#f58207] text-white rounded-br-md"
                      : "bg-white text-slate-700 border border-slate-200 rounded-bl-md"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-slate-200 bg-white space-y-3">
            <textarea
              ref={inputRef}
              data-testid="edit-image-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={"Ex: \"un élément est buggé sur l'image\""}
              className="w-full min-h-[120px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#f58207]/20 focus:border-[#f58207] resize-none"
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Ctrl/Cmd + Entrée pour valider, ou bouton ci-dessous pour envoyer.
              </p>
              <button
                type="button"
                data-testid="send-edit-prompt-button"
                onClick={submit}
                disabled={!prompt.trim()}
                className="h-11 px-5 rounded-full bg-[#f58207] text-white font-bold text-sm hover:bg-[#c86b05] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                Générer la nouvelle image
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
